import React, { useState } from "react";
import styled from "styled-components";
import {
  svgCPU,
  svgDisk,
  svgWire,
  svgMemory,
  svgLowTraffic,
  svgInvertedTraffic,
  svgNTP,
  svgLicense,
  svgBilling,
} from "./images";

const STATUS_LEVEL_MAP = {
  unknown: { color: "bg-grey", level: 0 },
  normal: { color: "bg-green", level: 1 },
  notice: { color: "bg-yellow-bqn", level: 2 },
  warning: { color: "bg-orange", level: 3 },
  critical: { color: "bg-red", level: 4 },
};

const STATUS_HEX_MAP = {
  unknown: "var(--signal-grey, #bcbcbc)",
  normal: "var(--signal-green, #00ba07)",
  notice: "var(--signal-yellow, #baa700)",
  warning: "var(--signal-yellow, #baa700)",
  critical: "var(--signal-red, #ba0000)",
};

const LEVEL_COLOR_MAP = {
  0: { color: "grey", status: "unknown" },
  1: { color: "green", status: "normal" },
  2: { color: "yellow-bqn", status: "notice" },
  3: { color: "orange", status: "warning" },
  4: { color: "red", status: "critical" },
};

const TooltipWrapper = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  color: white;
  font-size: 12px;
  padding: 6px;
  transform: translate(-50%, -50%);
  font-family: "AllianceNo2-Regular" !important;
`;

const TooltipContent = styled.div`
  margin: 4px 0;
  display: flex;
  align-items: center;
`;

const AlarmsWrapper = styled.div``;

const EntryWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ValueTooltip = styled.div`
  display: flex;
  display: flex;
  align-items: center;
`;

const LabelTooltip = styled.div`
  word-break: keep-all;
  white-space: nowrap;
`;

const Ball = styled.div`
  width: 8px;
  height: 8px;
  background: ${(props) => props.color || "transparent"};
  border-radius: 5px;
  margin: 0 5px 0 15px;
`;

const getMaxStatus = (alarms) => {
  return alarms.reduce((acc, value) => {
    const level = STATUS_LEVEL_MAP[value?.status]?.level;
    if (level != null && level > acc) {
      acc = level;
    }
    return acc;
  }, 0);
};

const TooltipEntry = ({ label, value }) => {
  return (
    <EntryWrapper>
      <LabelTooltip>{label}:</LabelTooltip>
      <ValueTooltip>
        <Ball color={STATUS_HEX_MAP[value]}></Ball>
        <div>{value}</div>
      </ValueTooltip>
    </EntryWrapper>
  );
};

const Tooltip = ({ alarms }) => {
  return (
    <TooltipWrapper
      style={{
        position: "absolute",
        width: "auto",
        left: 0,
        top: 0,
      }}
    >
      <TooltipContent>
        <AlarmsWrapper>
          {Object.entries(alarms).map(([key, value]) => {
            return <TooltipEntry label={key} key={key} value={value?.status} />;
          })}
        </AlarmsWrapper>
      </TooltipContent>
    </TooltipWrapper>
  );
};

const Alarm = ({ alarms, label, link, img }) => {
  const [hovered, setHovered] = useState(false);
  const maxStatus = getMaxStatus(Object.values(alarms));
  const classNameBox = `info-box-3 hover-zoom-effect hyperlink bg-${LEVEL_COLOR_MAP[maxStatus].color}`;

  const handleClick = () => {
    globalNavigate(link);
  };

  return (
    <div
      className="col-lg-4 col-md-4 col-sm-4 col-xs-4"
      onClick={handleClick}
      onMouseEnter={(e) => setHovered(true)}
      onMouseLeave={(e) => setHovered(false)}
    >
      <div className={classNameBox}>
        {img}
        <div className="content">
          <div className="text">{label}</div>
          <div id="dbCpu" className="number">
            {LEVEL_COLOR_MAP[maxStatus].status.toUpperCase()}
          </div>
          <div className="ball"></div>
        </div>
      </div>
      {Object.keys(alarms).length > 1 && hovered ? (
        <Tooltip alarms={alarms} />
      ) : null}
    </div>
  );
};

const Alarms = ({ data }) => {
  return (
    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
      <div className="row alarms">
        <Alarm
          alarms={{ CPU: data["cpu"] }}
          label="CPU"
          img={svgCPU}
          link="viewStatusSystemCPU"
        />
        <Alarm
          alarms={{ disk: data["disk"] }}
          label="Disk"
          img={svgDisk}
          link="viewStatusSystemDisk"
        />
        <Alarm
          alarms={{ wire: data["wire"] }}
          label="Network Interfaces"
          img={svgWire}
          link="viewStatusInterfacesDataWires"
        />
        <Alarm
          alarms={{
            "memory-dpdk": data["memory-dpdk"],
            "memory-pool": data["memory-pool"],
          }}
          label="Memory"
          img={svgMemory}
          link="viewStatusSystemMemory"
        />
        <Alarm
          alarms={{
            "traffic-downlink": data["traffic-downlink"],
            "traffic-uplink": data["traffic-uplink"],
            "traffic-low": data["traffic-low"],
          }}
          label="Low Traffic"
          img={svgLowTraffic}
          link="viewIfaceStkStats"
        />
        <Alarm
          alarms={{ "traffic-inverted": data["traffic-inverted"] }}
          label="Inverted Traffic"
          img={svgInvertedTraffic}
          link="viewIfaceStkStats"
        />
        <Alarm
          alarms={{ time: data["time"] }}
          label="NTP"
          img={svgNTP}
          link="viewSysNtp"
        />
        <Alarm
          alarms={{
            "license-available": data["license-available"],
            "license-expiration": data["license-expiration"],
            "license-usage": data["license-usage"],
            bqnmgr: data["bqnmgr"],
          }}
          label="License"
          img={svgLicense}
          link="viewLicense"
        />
        <Alarm
          alarms={{ billing: data["billing"] }}
          label="Billing Sync"
          img={svgBilling}
          link="viewStatusBilling"
        />
      </div>
    </div>
  );
};

export default Alarms;
